import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import {
  ArrowLeft, Camera, CheckCircle2, AlertTriangle, XCircle,
  Ticket, User, Loader2, ScanLine,
} from 'lucide-react';
import { useBranding } from '../../context/BrandingContext';
import { staffApi } from '../../services/api';
import OfflineBanner from '../../components/OfflineBanner';

/**
 * Dedicated event-ticket scanner for staff. Operator stays in scanner
 * mode and gets a clear visual state for each scan:
 *   ✅ green   → check-in completed
 *   ⚠️ amber  → already checked in (with timestamp)
 *   ❌ red    → invalid / wrong brand / unpaid / cancelled
 * After viewing the result, operator can tap "Scansiona prossimo" to
 * resume scanning without leaving the screen.
 */
export default function TicketScanner() {
  const navigate = useNavigate();
  const { primary_color, secondary_color, business_name, logo_url } = useBranding();
  const pc = primary_color || '#D44E20';
  const sc = secondary_color || '#3ECF8E';

  const [scanning, setScanning] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);  // { kind, message, ticket?, customer_name? }
  const [history, setHistory] = useState([]);  // [{ qr, kind, label, time }]
  const [error, setError] = useState(null);

  const scannerRef = useRef(null);
  const lastScanned = useRef(null);  // dedupe consecutive scans of same QR

  /* ── lifecycle: start/stop the camera ────────────────────────────── */
  const startScanner = useCallback(async () => {
    try {
      const html5Qr = new Html5Qrcode('ticket-qr-reader');
      scannerRef.current = html5Qr;
      await html5Qr.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 260, height: 260 } },
        async (decodedText) => {
          if (busy) return;
          if (lastScanned.current === decodedText) return;  // dedupe rapid re-detect
          const m = decodedText.match(/loyaltypos:ticket:.+/);
          if (!m) return;  // ignore non-ticket QRs (e.g. customer cards)
          lastScanned.current = decodedText;
          setBusy(true);
          await html5Qr.stop().catch(() => {});
          setScanning(false);
          await performCheckin(decodedText);
          setBusy(false);
        },
        () => {},
      );
      setError(null);
    } catch (err) {
      setError("Impossibile accedere alla fotocamera. Verifica i permessi.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy]);

  const stopScanner = useCallback(async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); } catch { /* noop */ }
      scannerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (scanning) {
      startScanner();
    }
    return () => { stopScanner(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanning]);

  /* ── API: check-in the scanned QR ────────────────────────────────── */
  const performCheckin = async (qrCode) => {
    try {
      const res = await staffApi.post('/events/tickets/checkin', { qr_code: qrCode });
      const data = res.data || {};
      const time = new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
      if (data.status === 'success') {
        const r = { kind: 'ok', message: data.message, ticket: data.ticket, customer_name: data.customer_name, qr: qrCode };
        setResult(r);
        setHistory(h => [{
          qr: qrCode,
          kind: 'ok',
          label: `${data.customer_name || 'Ospite'} · ${data.ticket?.tier_name || ''}`,
          time,
        }, ...h].slice(0, 8));
      } else if (data.status === 'already_checked_in') {
        const r = { kind: 'used', message: data.message, ticket: data.ticket, qr: qrCode };
        setResult(r);
        setHistory(h => [{
          qr: qrCode,
          kind: 'used',
          label: `Già usato · ${data.ticket?.tier_name || ''}`,
          time,
        }, ...h].slice(0, 8));
      }
    } catch (e) {
      const detail = e.response?.data?.detail || 'Biglietto non valido';
      const time = new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
      setResult({ kind: 'invalid', message: detail, qr: qrCode });
      setHistory(h => [{ qr: qrCode, kind: 'invalid', label: detail, time }, ...h].slice(0, 8));
    }
  };

  const scanNext = () => {
    setResult(null);
    lastScanned.current = null;
    setScanning(true);
  };

  /* ── render: full-bleed dark overlay always; result card or camera ── */
  const headerColor = !result ? '#0F172A' : (
    result.kind === 'ok' ? '#0E3F2E' :
    result.kind === 'used' ? '#3F2E0E' :
    '#3F0E1A'
  );

  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ backgroundColor: headerColor }} data-testid="ticket-scanner-screen">
      <OfflineBanner />

      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-6 pb-3">
        <button onClick={() => navigate('/staff/home')} data-testid="ticket-scanner-back"
          className="w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center active:scale-95">
          <ArrowLeft size={20} />
        </button>
        <div className="flex items-center gap-2">
          {logo_url && <img src={logo_url} alt="" className="w-7 h-7 rounded-md" />}
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wider text-white/50">{business_name || 'Lazo'}</p>
            <p className="text-sm font-bold text-white">Check-in biglietti</p>
          </div>
        </div>
      </div>

      {/* Main */}
      <div className="flex-1 overflow-y-auto px-4 pb-6">
        {/* Scanner */}
        {scanning && !result && (
          <div className="max-w-sm mx-auto mt-4">
            {error ? (
              <div className="text-center text-white py-12">
                <div className="w-14 h-14 mx-auto rounded-full bg-red-500/20 flex items-center justify-center mb-3">
                  <Camera size={26} className="text-red-400" />
                </div>
                <p className="font-semibold">{error}</p>
                <p className="text-xs text-white/60 mt-1">Assicurati che il browser abbia accesso alla fotocamera.</p>
              </div>
            ) : (
              <>
                <div className="rounded-3xl overflow-hidden border-2" style={{ borderColor: `${pc}80` }}>
                  <div id="ticket-qr-reader" />
                </div>
                <div className="flex items-center justify-center gap-2 mt-4 text-white/70 text-sm">
                  <ScanLine size={14} />
                  <span>Inquadra il QR del biglietto</span>
                </div>
              </>
            )}
          </div>
        )}

        {/* Loading mid-call */}
        {busy && (
          <div className="flex items-center justify-center py-16 text-white">
            <Loader2 className="animate-spin" size={28} />
          </div>
        )}

        {/* Result */}
        {result && !busy && (
          <div className="max-w-sm mx-auto mt-2 animate-fade-in-up" data-testid={`ticket-result-${result.kind}`}>
            {/* Status icon */}
            <div className="flex flex-col items-center text-center mb-4">
              {result.kind === 'ok' && (
                <>
                  <div className="w-20 h-20 rounded-full bg-emerald-500/25 flex items-center justify-center mb-3">
                    <CheckCircle2 size={48} className="text-emerald-400" />
                  </div>
                  <p className="text-xl font-black text-white">Check-in OK</p>
                  <p className="text-sm text-emerald-200 mt-1">Ingresso autorizzato</p>
                </>
              )}
              {result.kind === 'used' && (
                <>
                  <div className="w-20 h-20 rounded-full bg-amber-500/25 flex items-center justify-center mb-3">
                    <AlertTriangle size={48} className="text-amber-400" />
                  </div>
                  <p className="text-xl font-black text-white">Già utilizzato</p>
                  <p className="text-sm text-amber-200 mt-1">{result.message}</p>
                </>
              )}
              {result.kind === 'invalid' && (
                <>
                  <div className="w-20 h-20 rounded-full bg-red-500/25 flex items-center justify-center mb-3">
                    <XCircle size={48} className="text-red-400" />
                  </div>
                  <p className="text-xl font-black text-white">Non valido</p>
                  <p className="text-sm text-red-200 mt-1">{result.message}</p>
                </>
              )}
            </div>

            {/* Ticket details (when present) */}
            {result.ticket && (
              <div className="rounded-2xl bg-white/8 backdrop-blur border border-white/10 p-4 space-y-2.5" data-testid="ticket-detail-card">
                <Row icon={<Ticket size={14} />} label="Evento" value={result.ticket.event_name || '—'} />
                <Row icon={<Ticket size={14} />} label="Fascia" value={result.ticket.tier_name || '—'} highlight={pc} />
                {(result.customer_name || result.ticket.buyer_first_name) && (
                  <Row icon={<User size={14} />} label="Acquirente"
                    value={result.customer_name || `${result.ticket.buyer_first_name || ''} ${result.ticket.buyer_last_name || ''}`.trim() || '—'} />
                )}
                {result.ticket.event_date && (
                  <Row icon={<Ticket size={14} />} label="Data evento" value={`${result.ticket.event_date}${result.ticket.event_time ? ` · ${result.ticket.event_time}` : ''}`} />
                )}
              </div>
            )}

            {/* CTA */}
            <button onClick={scanNext} data-testid="ticket-scan-next"
              className="mt-5 w-full h-13 rounded-2xl text-white font-bold text-sm flex items-center justify-center gap-2 active:scale-[0.98]"
              style={{ backgroundColor: pc, height: 52 }}>
              <ScanLine size={16} /> Scansiona prossimo biglietto
            </button>
          </div>
        )}

        {/* History (last 8 scans) */}
        {history.length > 0 && (
          <div className="max-w-sm mx-auto mt-6" data-testid="ticket-scan-history">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-white/40 mb-2 px-1">Ultimi scan</p>
            <div className="space-y-1.5">
              {history.map((h, i) => {
                const dotColor = h.kind === 'ok' ? sc : h.kind === 'used' ? '#FBBF24' : '#EF4444';
                return (
                  <div key={`${h.qr}-${i}`} className="flex items-center gap-3 rounded-xl px-3 py-2 bg-white/5 border border-white/8">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: dotColor }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-white truncate">{h.label}</p>
                    </div>
                    <span className="text-[10px] text-white/40 tabular-nums">{h.time}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ icon, label, value, highlight }) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="w-7 h-7 rounded-lg bg-white/8 flex items-center justify-center flex-shrink-0 text-white/70">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-white/40">{label}</p>
        <p className="text-sm font-semibold mt-0.5 break-words" style={{ color: highlight || '#FFFFFF' }}>{value}</p>
      </div>
    </div>
  );
}
